import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatterDispatch } from '@state/matter';
import { Observable } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import { addressString } from '@core/util/address';
import { Address } from '@models/address';
import { Brokerage, MatterFileType } from '@models/matter';
import { CreateOrUpdateBrokerageModel } from '../create/create.component';
import { openClose } from '@core/animations/animations';
import { ActivatedRoute, Router } from '@angular/router';
import { Employee } from '@models/company';
import { AlgoliaBroker } from '@fnModels/algolia/broker';
import { TestHooks } from './brokerage-side-form.hooks';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'app-brokerage-side-form',
	templateUrl: './brokerage-side-form.component.html',
	animations: [openClose(300)],
	standalone: false,
})
export class BrokerageSideFormComponent {
	testHooks = TestHooks;
	@Output() onFocus = new EventEmitter<string>();
	@Input() label: string;
	@Input() collapsed = false;
	@Input() skeletonLoading = false;
	@Input() brokerageFieldType: string;

	@Input() set isClientBrokerage(isClient: boolean) {
		this.isClient = isClient;
		if (isClient) {
			this.clientBrokerage$
				.pipe(untilDestroyed(this))
				.subscribe((brokerage) => {
					this.brokerage = brokerage;
				});
		} else {
			this.otherPartyBrokerage$
				.pipe(untilDestroyed(this))
				.subscribe((brokerage) => {
					this.brokerage = brokerage;
				});
		}
	}

	@SelectSnapshot(SelectedMatter.id) saleId;

	@Select(SelectedMatter.clientBrokerage)
	clientBrokerage$: Observable<Brokerage>;
	@Select(SelectedMatter.otherPartyBrokerage)
	otherPartyBrokerage$: Observable<Brokerage>;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	public showAiFlags$ = this.store.select(SelectedMatter.getFieldMappings).pipe(
		map((fieldMappings) => {
			let brokerage;

			if (this.isClient) {
				if (this.matterFileType === MatterFileType.PURCHASE) {
					brokerage = fieldMappings?.buyerBrokerage;
				} else if (this.matterFileType === MatterFileType.SALE) {
					brokerage = fieldMappings?.sellerBrokerage;
				}
			} else {
				if (this.matterFileType === MatterFileType.SALE) {
					brokerage = fieldMappings?.buyerBrokerage;
				} else if (this.matterFileType === MatterFileType.PURCHASE) {
					brokerage = fieldMappings?.sellerBrokerage;
				}
			}

			return {
				showBrokerageAiIcon: !!brokerage?.name,
				showBrokerAiIcon: !!brokerage?.broker,
			};
		}),
	);

	public isClient: boolean;
	public ICON = ICON;
	public hoverShadow = true;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;
	public brokerage: Brokerage;
	public brokerageModel: CreateOrUpdateBrokerageModel;
	public showCreateForm = false;
	public searchedValue = '';
	constructor(
		private matterDispatch: MatterDispatch,
		private router: Router,
		private route: ActivatedRoute,
		private store: Store,
	) {}

	setAddress(address: Address): string {
		return addressString(address);
	}

	_onFocus() {
		this.onFocus.emit(this.brokerageFieldType);
	}

	removeBrokerage() {
		this.matterDispatch.RemoveBrokerage(
			this.saleId,
			this.isClient,
			this.matterFileType,
		);
		this._onFocus();
	}

	selectBrokerage = (brokerage: Brokerage) => {
		this.matterDispatch.SelectBrokerage(
			this.saleId,
			brokerage.id,
			this.isClient,
			this.matterFileType,
			brokerage.leap?.cardId,
		);
	};

	updateBrokerage() {
		this.router.navigate(['edit-brokerage'], {
			relativeTo: this.route,
			state: { data: { brokerage: this.brokerage, isClient: this.isClient } },
		});
	}

	selectBroker = (broker: AlgoliaBroker) =>
		this.matterDispatch.SelectBroker(
			this.saleId,
			broker.brokerageId,
			broker.id,
			this.isClient,
			this.matterFileType,
			broker.leap?.personId,
		);

	createBroker = (broker: Employee) => {
		this.matterDispatch.CreateBroker(
			this.saleId,
			broker,
			this.isClient,
			this.brokerage.id,
			this.matterFileType,
		);
	};

	removeBroker = () =>
		this.matterDispatch.RemoveBroker(
			this.saleId,
			this.isClient,
			this.matterFileType,
		);

	brokerageDropdownOptionSelect(event) {
		switch (event.result.name) {
			case 'Edit':
				this.updateBrokerage();
				break;
			case 'Remove':
				this.removeBrokerage();
				break;
		}
	}

	get sellerBuyerBrokerLabel() {
		if (this.matterFileType === MatterFileType.PURCHASE) {
			return this.isClient ? 'Buyer' : 'Seller';
		}

		return this.isClient ? 'Seller' : 'Buyer';
	}

	_onCreate($event) {
		this.searchedValue = $event.name;
		this.showCreateForm = true;
	}

	onCreated(brokerage: Brokerage) {
		this.showCreateForm = false;
		this.brokerage = brokerage;
	}

	onCancel() {
		this.showCreateForm = false;
	}
}
